import {
  dailySeed,
  generateDailyRun,
  rotationIndex,
  type EnemyTable,
  type GenData,
  type GeneratedMap,
  type MutatorSpec,
  type RunEffect,
  type RunConfig,
  type WaveSpec,
} from '@prism/sim';
import { BUFF_TILES } from './data/buffTiles.js';
import { buildEnemyTable, buildRunConfig } from './scenario.js';
import { compileMap } from './autotile.js';
import { GEN_DATA } from './data/gen.js';
import { MAPS } from './data/maps.js';
import { TOWERS } from './data/towers.js';
import type { MapDef } from './schemas/defs.js';

/**
 * Daily run configuration (TRD-10 §4, TRD-13, TRD-08 §9).
 *
 * Everything derives from the UTC date. The client and the server both call this — the
 * server never trusts a config the client supplies (TRD-11 R-11-06) — so a divergence here
 * becomes a rejected submission rather than an exploit.
 *
 * ── The unlock exception ──────────────────────────────────────────────────────────────
 * Daily runs use a FIXED pool: all 30 towers, all 10 classes, no start bonuses, whatever
 * the player's meta tree says (R-08-22). Otherwise the leaderboard would rank unlock
 * progress rather than skill.
 */

export interface DailyRun {
  date: string;
  seed: bigint;
  config: RunConfig;
  /** The generated map in renderable form, or an authored map when generation fell back. */
  map: MapDef;
  mutator: MutatorSpec | null;
  generated: boolean;
  /** How far the reference player got — surfaced for telemetry (TRD-13 R-13-38). */
  probeWave: number;
  attempts: number;
}

/**
 * `GenData` in the shape the sim's generator consumes.
 *
 * Field-by-field on purpose (the sim cannot import content types), and therefore the
 * exact place a newly authored tuning knob goes missing: it is authored, it validates,
 * it appears in the data file, and it is never handed to the generator. Anything added
 * to `GEN_DATA.tuning` has to be added here too.
 */
function genData(): GenData {
  const biome = GEN_DATA.biomes[0];
  return {
    threatCosts: GEN_DATA.threatCosts,
    archetypeGates: GEN_DATA.archetypeGates,
    bossPool: GEN_DATA.bossPool,
    ...(GEN_DATA.finalBoss !== undefined ? { finalBoss: GEN_DATA.finalBoss } : {}),
    tuning: {
      waveCount: GEN_DATA.tuning.waveCount,
      baseThreat: GEN_DATA.tuning.baseThreat,
      threatGrowth: GEN_DATA.tuning.threatGrowth,
      lateThreatGrowth: GEN_DATA.tuning.lateThreatGrowth,
      lateGrowthFromWave: GEN_DATA.tuning.lateGrowthFromWave,
      spikeMultiplier: GEN_DATA.tuning.spikeMultiplier,
      reliefMultiplier: GEN_DATA.tuning.reliefMultiplier,
    },
    pathLength: GEN_DATA.tuning.pathLength,
    terrainDensity: [biome?.terrainDensity ?? 0.16, (biome?.terrainDensity ?? 0.16) + 0.06],
    buffTileKinds: BUFF_TILES.map((t) => ({
      id: t.id,
      weight: t.weight,
      ...(t.grantsClass !== undefined ? { grantsClass: t.grantsClass } : {}),
      ...(t.rangeBonus !== undefined ? { rangeBonus: t.rangeBonus } : {}),
      ...(t.onHitStatus !== undefined ? { onHitStatus: t.onHitStatus } : {}),
      ...(t.coreHpEveryNWaves !== undefined ? { coreHpEveryNWaves: t.coreHpEveryNWaves } : {}),
    })),
    buffTileCount: [1, 4],
    maxAttempts: GEN_DATA.tuning.maxAttempts,
    probeTargetWave: GEN_DATA.tuning.probeTargetWave,
    mutators: GEN_DATA.mutators.map((m) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      threatBudgetMultiplier: m.threatBudgetMultiplier,
      weight: m.weight,
    })),
  };
}

/** The authored effect payload for a rolled mutator, as run-effect bundles (R-08-22b). */
function mutatorRunEffects(mutator: MutatorSpec | null): RunEffect[] {
  if (!mutator) return [];
  const authored = GEN_DATA.mutators.find((m) => m.id === mutator.id);
  if (!authored || authored.effects.length === 0) return [];
  return [{ label: mutator.name, effects: authored.effects }];
}

/**
 * The enemy table with the mutator's flat stat modifiers applied.
 *
 * These were authored from day one and applied NOWHERE — Frostbite's "+20% speed",
 * Swarm Day's "−45% health" and Fortified's "+4 armor" were banner text while the
 * threat-budget half did all the actual work. The probe plays this same table, so the
 * solvability bar re-validates every mutator with its modifiers really in force.
 */
function mutatedEnemyTable(mutator: MutatorSpec | null): EnemyTable {
  const base = buildEnemyTable();
  const authored = mutator ? GEN_DATA.mutators.find((m) => m.id === mutator.id) : undefined;
  if (!authored || authored.enemyModifiers.length === 0) return base;

  const table: Record<string, EnemyTable[string]> = {};
  for (const [id, spec] of Object.entries(base)) {
    const modified = { ...spec };
    for (const mod of authored.enemyModifiers) {
      const current = modified[mod.stat];
      modified[mod.stat] = mod.op === 'mul' ? current * mod.value : current + mod.value;
    }
    // Armor adds can push below zero on 0-armor enemies via future negative mods; clamp
    // the floor so a modifier can never mint negative stats.
    modified.hp = Math.max(1, modified.hp);
    modified.armor = Math.max(0, modified.armor);
    modified.speed = Math.max(0.05, modified.speed);
    table[id] = modified;
  }
  return table;
}

/** Turn a generated map into the renderable `MapDef`, via the same autotiler authored maps use. */
function toMapDef(id: string, generated: GeneratedMap): MapDef {
  const compiled = compileMap({
    id,
    name: 'Daily',
    biome: GEN_DATA.biomes[0]?.id ?? 'ashen-flat',
    paths: generated.paths.map((p) => ({ id: p.id, waypoints: [...p.waypoints] })),
    core: generated.core,
    blockers: generated.blockers,
  });
  // The generator's mask is authoritative for gameplay — it is what the probe played and
  // what the server will replay. The compiler's tile layers are for rendering only.
  return { ...compiled, buildMask: generated.buildMask, generated: true };
}

export function deriveDailyRun(date: string): DailyRun {
  const seed = dailySeed(date);
  const towerPool = TOWERS.map((t) => t.id);
  const id = `daily-${date}`;

  const configFor = (
    map: GeneratedMap,
    waves: WaveSpec[],
    mutator: MutatorSpec | null,
  ): RunConfig =>
    buildRunConfig({
      map: { id, paths: map.paths, core: map.core, buildMask: map.buildMask },
      isDaily: true,
      towerPool,
      waves,
      overrides: {
        // The generated map's buff tiles. Hand-copied like every other content→sim
        // bridge, and therefore the exact place a newly authored tile field goes missing.
        buffTiles: map.buffTiles,
        // R-08-22b, finally wired: the mutator's effects ride the same run-effect seam
        // the meta tree uses. Before this, a mutator's `effects` array reached nothing —
        // Monoculture's threshold shift and friends were display text. The generator's
        // MutatorSpec deliberately stays lean (id/threat/weight — all the ROLL needs),
        // so the effects are looked up here from the authored data by id.
        runEffects: mutatorRunEffects(mutator),
        // Same story for the flat enemy stat modifiers — authored, validated, never
        // applied until now. See mutatedEnemyTable.
        enemyTable: mutatedEnemyTable(mutator),
      },
    });

  const result = generateDailyRun(seed, {
    data: genData(),
    // The probe plays exactly the config the player will get — same function, no
    // approximation, so a seed cannot pass the probe and then hand out something else.
    toRunConfig: (map, waves, mutator) => configFor(map, waves, mutator),
  });

  if (!result.exhausted) {
    return {
      date,
      seed,
      config: configFor(result.map, result.waves, result.mutator),
      map: toMapDef(id, result.map),
      mutator: result.mutator,
      generated: true,
      probeWave: result.probeWave,
      attempts: result.attempts,
    };
  }

  // Fallback: an authored map with the authored wave table (R-13-33). This exists anyway
  // for normal runs, so the generator's safety net costs nothing extra — and a daily that
  // is merely unexciting beats a daily that is unplayable.
  const map = MAPS[rotationIndex(seed, MAPS.length)] ?? MAPS[0];
  if (!map) throw new Error('no maps available for daily fallback');

  return {
    date,
    seed,
    config: buildRunConfig({ mapId: map.id, isDaily: true, towerPool }),
    map,
    mutator: null,
    generated: false,
    probeWave: result.probeWave,
    attempts: result.attempts,
  };
}

/** Adapter matching the server's `DailyGenerator` seam (TRD-11 §5). */
export function createDailyGenerator() {
  return {
    generate(date: string): { date: string; seed: string; config: RunConfig } {
      const run = deriveDailyRun(date);
      return { date: run.date, seed: run.seed.toString(10), config: run.config };
    },
  };
}

export type { WaveSpec };
